import { CSSProperties } from 'react';
import { LockKeyhole } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useActiveConversation } from '../../chat/ActiveConversationContext';
import { Button, Card, Typography } from '../ui';
import styles from './BriefSlot.module.css';

// Mobile-card rendering of the "re-enable morning meetings" brief state — the flat desktop content
// (`ActivateBrief`) presented as a floating `Card`, matching the native `BriefActivateCard`
// (Figma 2057:12397): a lock eyebrow, the plain heavy-sans headline "Don't miss what happened
// overnight", body copy, and a solid-black "Enable Now" CTA that opens a chat.

// Headline — the heavy display sans, plain (no serif accent run): Archivo SemiBold 32/32.
const HEADLINE_SANS: CSSProperties = {
  fontFamily: 'var(--brand-font-display)',
  fontSize: 32,
  fontWeight: 'var(--sym-weight-semibold)',
  lineHeight: '32px',
  letterSpacing: '-0.96px',
  color: 'rgb(var(--ink-rgb))',
};

// Body copy — Wix Madefor Text 14/20, -0.42px, black. Pinned inline because the `.sym-*` type variants
// out-specify the module class and carry 18px / -0.02em.
const BODY_TEXT: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 14,
  fontWeight: 'var(--sym-weight-semibold)',
  lineHeight: '20px',
  letterSpacing: '-0.42px',
  color: 'rgb(var(--ink-rgb))',
  fontFeatureSettings: "'dlig' on",
};

export function MobileActivateBrief() {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const { openChatWithMessage } = useActiveConversation();

  return (
    <Card component="section" style={{ textAlign: 'start' }} aria-labelledby="brief-activate-title">
      <p className={styles.eyebrow}>
        <LockKeyhole size={16} aria-hidden />
        <Typography variant="h4" color="strong" component="span">
          {t('brief.activate.eyebrow')}
        </Typography>
      </p>
      <div id="brief-activate-title" className={styles.mobileHeadline} style={HEADLINE_SANS}>
        {t('brief.activate.title')}
      </div>
      <p className={styles.mobileBody} style={BODY_TEXT}>
        {t('brief.activate.body')}
      </p>
      <div className={styles.mobileCta}>
        <Button
          data-card-primary
          onClick={() => {
            logPageClickBi({ page_name: 'home', button_name: 'brief_activate' });
            openChatWithMessage({ kind: 'new', message: t('brief.activate.chatMessage') });
          }}
        >
          {t('brief.activate.cta')}
        </Button>
      </div>
    </Card>
  );
}
